import { useEffect, useRef } from 'react';
import { purchasingAPI } from '../../lib/api';
import type { UserProfile } from '../../lib/api';
import { useView } from '../../contexts/ViewContext';
import type { NCRFormData } from '../../components/quality/ncr-create/NCRDetailsForm';

/** How long to wait after the last keystroke in PO No. / PO Line No. before looking the order up. */
const TYPING_PAUSE_MS = 600;

/**
 * CR-7138: pick the NCR's Buyer from the PO line the NCR is raised against —
 * the line a GRN lookup linked, or the one a typed PO No. (and PO Line No.)
 * identifies in the user's view.
 *
 * The line carries its buyer as the ERP buyer code; the Buyer that is picked is
 * the customer user who holds that code. Only a Buyer this hook picked, or an
 * empty one, is ever replaced — a Buyer the user chose stays. Nothing is picked
 * when the order does not identify one line, the line has no buyer, or no user
 * holds its code, and a Buyer this hook picked for a previous order is cleared.
 *
 * @example
 * useBuyerAutofill(formData, updateField, customerUsers);
 *
 * @param formData - the Create NCR form state
 * @param updateField - the form's field setter
 * @param customerUsers - the users offered in the Buyer list
 */
export function useBuyerAutofill(
  formData: NCRFormData,
  updateField: (field: keyof NCRFormData, value: string) => void,
  customerUsers: UserProfile[],
): void {
  const { getViewQueryParams } = useView();
  const autoPicked = useRef<string | null>(null);
  const latest = useRef(0);
  // Read at answer time, so the effect below only re-runs when the order changes.
  const buyer = useRef(formData.buyer_name);
  buyer.current = formData.buyer_name;
  const setField = useRef(updateField);
  setField.current = updateField;
  const viewParams = useRef(getViewQueryParams);
  viewParams.current = getViewQueryParams;

  const poLineId = formData.po_line;
  const poNumber = formData.po_number.trim();
  const lineNumber = formData.po_line_number.trim();

  useEffect(() => {
    const request = ++latest.current;
    if (!poLineId && !poNumber) {
      // No order on the form any more: a Buyer picked for the previous one goes too.
      const now = buyer.current.trim();
      if (autoPicked.current && now === autoPicked.current) setField.current('buyer_name', '');
      autoPicked.current = null;
      return undefined;
    }
    const timer = window.setTimeout(async () => {
      try {
        let lineId = poLineId;
        if (!lineId) {
          const found = await purchasingAPI.getLines({
            po__po_number: poNumber,
            ...(lineNumber ? { line_number: lineNumber } : {}),
            page_size: 2,
            // Same as the GRN lookup: an NCR may be raised on any buyer's order,
            // not only the lines in the user's own buyer codes.
            all_suppliers: true,
            ...viewParams.current(),
          });
          const lines = found.data.results;
          lineId = lines.length === 1 ? lines[0].id : null;
        }
        const code = lineId ? ((await purchasingAPI.getLine(lineId)).data.buyer_id || '').trim() : '';
        // CR-7714: only when exactly one user holds the code. With several,
        // the first in the list was picked and saved on the NCR as if known.
        const holders = code
          ? customerUsers.filter((u) => (u.buyer_codes || []).includes(code))
          : [];
        const holder = holders.length === 1 ? holders[0] : undefined;
        const name = holder ? `${holder.user.first_name} ${holder.user.last_name}`.trim() : '';

        // A newer order has been entered, or the user has picked a Buyer themselves.
        const now = buyer.current.trim();
        if (request !== latest.current || (now && now !== autoPicked.current)) return;
        if (name) {
          autoPicked.current = name;
          setField.current('buyer_name', name);
        } else if (autoPicked.current) {
          autoPicked.current = null;
          setField.current('buyer_name', '');
        }
      } catch (err: unknown) {
        console.error('Buyer lookup failed:', err);
      }
    }, poLineId ? 0 : TYPING_PAUSE_MS);
    return () => window.clearTimeout(timer);
  }, [poLineId, poNumber, lineNumber, customerUsers]);
}
